import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { ChevronRight, Zap, Target, RefreshCw, ArrowRight, Menu, X, Send } from "lucide-react";

// Contact Modal Component
const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    country: "",
    requirement: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Project Inquiry from ${formData.name} - ${formData.organization}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\nOrganization: ${formData.organization}\nCountry: ${formData.country}\n\nRequirement:\n${formData.requirement}`
    );
    window.location.href = `mailto:hello@stragmato.com?subject=${subject}&body=${body}`;
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid="contact-modal-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[var(--bg-darker)]/95 backdrop-blur-md" />
      
      {/* Modal */}
      <div
        data-testid="contact-modal"
        className="relative w-full max-w-lg bg-gradient-to-b from-[var(--iron-darker)] to-[var(--bg-darker)] border border-[var(--iron)] p-8 animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Corner Accents */}
        <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[var(--laser-red)]" />
        <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-[var(--laser-red)]" />
        <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-[var(--laser-red)]" />
        <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-[var(--laser-red)]" />

        {/* Close Button */}
        <button
          data-testid="contact-modal-close"
          onClick={onClose}
          className="absolute top-4 right-4 text-[var(--steel)] hover:text-[var(--laser-red)] transition-colors"
        >
          <X size={24} />
        </button>

        {/* Header */}
        <h3 className="headline text-2xl text-[var(--white)] mb-2">Start Your Project</h3>
        <p className="body-text text-sm text-[var(--steel-light)] mb-8">Tell us about your vision and we'll get back to you.</p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="body-text text-xs uppercase tracking-wider text-[var(--steel)] mb-2 block">Name *</label>
              <input
                type="text"
                name="name"
                required
                data-testid="contact-input-name"
                value={formData.name}
                onChange={handleChange}
                className="w-full bg-[var(--bg-darker)] border border-[var(--iron)] px-4 py-3 text-[var(--white)] body-text text-sm focus:border-[var(--laser-red)] focus:outline-none transition-colors"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="body-text text-xs uppercase tracking-wider text-[var(--steel)] mb-2 block">Email *</label>
              <input
                type="email"
                name="email"
                required
                data-testid="contact-input-email"
                value={formData.email}
                onChange={handleChange}
                className="w-full bg-[var(--bg-darker)] border border-[var(--iron)] px-4 py-3 text-[var(--white)] body-text text-sm focus:border-[var(--laser-red)] focus:outline-none transition-colors"
                placeholder="you@company.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="body-text text-xs uppercase tracking-wider text-[var(--steel)] mb-2 block">Organization</label>
              <input
                type="text"
                name="organization"
                data-testid="contact-input-org"
                value={formData.organization}
                onChange={handleChange}
                className="w-full bg-[var(--bg-darker)] border border-[var(--iron)] px-4 py-3 text-[var(--white)] body-text text-sm focus:border-[var(--laser-red)] focus:outline-none transition-colors"
                placeholder="Company name"
              />
            </div>
            <div>
              <label className="body-text text-xs uppercase tracking-wider text-[var(--steel)] mb-2 block">Country</label>
              <input
                type="text"
                name="country"
                data-testid="contact-input-country"
                value={formData.country}
                onChange={handleChange}
                className="w-full bg-[var(--bg-darker)] border border-[var(--iron)] px-4 py-3 text-[var(--white)] body-text text-sm focus:border-[var(--laser-red)] focus:outline-none transition-colors"
                placeholder="Your country"
              />
            </div>
          </div>

          <div>
            <label className="body-text text-xs uppercase tracking-wider text-[var(--steel)] mb-2 block">What are you looking for? *</label>
            <textarea
              name="requirement"
              required
              rows={4}
              data-testid="contact-input-requirement"
              value={formData.requirement}
              onChange={handleChange}
              className="w-full bg-[var(--bg-darker)] border border-[var(--iron)] px-4 py-3 text-[var(--white)] body-text text-sm focus:border-[var(--laser-red)] focus:outline-none transition-colors resize-none"
              placeholder="Describe your project requirements..."
            />
          </div>

          <button
            type="submit"
            data-testid="contact-submit-btn"
            className="btn-industrial btn-primary-ind w-full group"
          >
            <span>Send Message</span>
            <Send className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </form>
      </div>
    </div>
  );
};

// Navigation Component
const Navigation = ({ onOpenContact }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#why-us", label: "Why Us" },
    { href: "#services", label: "Services" },
    { href: "#process", label: "Process" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        isScrolled
          ? "bg-[var(--bg-darker)]/95 backdrop-blur-md border-b border-[var(--iron)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo - More Prominent */}
          <a
            href="#"
            data-testid="nav-logo"
            className="logo-prominent text-3xl tracking-tight text-[var(--white)] hover:text-[var(--laser-red)] transition-colors duration-300"
          >
            <span className="font-black italic">STRAG</span><span className="font-light">MATO</span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                className="body-text text-sm uppercase tracking-widest text-[var(--steel-light)] hover:text-[var(--laser-red)] transition-colors duration-300"
              >
                {link.label}
              </a>
            ))}
            <button
              onClick={onOpenContact}
              data-testid="nav-cta-button"
              className="btn-industrial btn-primary-ind text-xs py-3 px-6"
            >
              Start Project
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-[var(--steel-light)] hover:text-[var(--laser-red)] transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          data-testid="mobile-menu"
          className={`md:hidden overflow-hidden transition-all duration-500 ${
            isMobileMenuOpen ? "max-h-96 pb-8" : "max-h-0"
          }`}
        >
          <div className="flex flex-col gap-6 pt-4 border-t border-[var(--iron)]">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                className="body-text text-lg text-[var(--steel-light)] hover:text-[var(--laser-red)] transition-colors duration-300"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                onOpenContact();
              }}
              data-testid="mobile-cta-button"
              className="btn-industrial btn-primary-ind text-sm py-4 w-full text-center"
            >
              Start Project
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = ({ onOpenContact }) => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden grid-pattern noise-overlay"
    >
      {/* Pulsing Laser Red Accent Lines */}
      <div className="pulse-line-vertical absolute top-0 left-[10%] w-px h-full" />
      <div className="pulse-line-vertical absolute top-0 left-[30%] w-px h-full" style={{ animationDelay: "1s" }} />
      <div className="pulse-line-horizontal absolute top-[20%] left-0 w-full h-px" style={{ animationDelay: "0.5s" }} />
      <div className="pulse-line-horizontal absolute top-[70%] left-0 w-full h-px" style={{ animationDelay: "1.5s" }} />
      
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[var(--laser-red)]/5 to-transparent pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[var(--laser-red)]/10 blur-[120px] pointer-events-none" />
      
      {/* Corner Accents */}
      <div className="absolute top-20 left-6 lg:left-16 w-px h-32 bg-gradient-to-b from-[var(--laser-red)] to-transparent" />
      <div className="absolute top-20 left-6 lg:left-16 w-16 h-px bg-gradient-to-r from-[var(--laser-red)] to-transparent" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20 w-full">
        <div className="grid lg:grid-cols-3 gap-12 items-center">
          {/* Main Content - Takes 2 columns */}
          <div className="lg:col-span-2">
            {/* Badge */}
            <div
              data-testid="hero-badge"
              className="inline-flex items-center gap-3 mb-8 animate-slideIn opacity-0"
            >
              <span className="w-8 h-px bg-[var(--laser-red)]" />
              <span className="body-text text-xs uppercase tracking-[0.3em] text-[var(--laser-red)]">AI-Powered Product Engineering</span>
            </div>

            {/* Headline */}
            <h1
              data-testid="hero-headline"
              className="headline text-5xl md:text-7xl lg:text-8xl leading-[0.9] mb-8 animate-slideUp opacity-0 delay-100"
            >
              <span className="text-[var(--white)]">Transform</span>
              <br />
              <span className="text-outline">Vision Into</span>
              <br />
              <span className="text-[var(--laser-red)] text-glow-red">Reality</span>
              <span className="text-[var(--steel)]">, Faster</span>
            </h1>

            {/* Subtitle */}
            <p
              data-testid="hero-subtitle"
              className="body-text text-lg md:text-xl text-[var(--steel-light)] max-w-2xl mb-12 animate-slideUp opacity-0 delay-200"
            >
              We leverage AI across every stage of the SDLC to accelerate your product journey from concept to launch—delivering exceptional quality at unprecedented speed.
            </p>

            {/* CTA Buttons */}
            <div
              data-testid="hero-cta-group"
              className="flex flex-col sm:flex-row gap-4 animate-slideUp opacity-0 delay-300"
            >
              <button
                onClick={onOpenContact}
                data-testid="hero-primary-cta"
                className="btn-industrial btn-primary-ind group"
              >
                <span>Start Your Project</span>
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="#services"
                data-testid="hero-secondary-cta"
                className="btn-industrial btn-secondary-ind"
              >
                Explore Services
              </a>
            </div>
          </div>

          {/* Side Stats - Now in its own column */}
          <div className="hidden lg:flex flex-col gap-8 items-end justify-center">
            {[
              { value: "3x", label: "Faster" },
              { value: "60%", label: "Cost Cut" },
              { value: "99.9%", label: "Quality" },
            ].map((stat, index) => (
              <div
                key={stat.value}
                data-testid={`hero-stat-${index + 1}`}
                className="text-right animate-fadeIn opacity-0 p-4 border-r-2 border-[var(--iron)] hover:border-[var(--laser-red)] transition-colors duration-300"
                style={{ animationDelay: `${0.5 + index * 0.2}s` }}
              >
                <div className="headline text-4xl text-[var(--laser-red)] text-glow-red">{stat.value}</div>
                <div className="body-text text-xs uppercase tracking-wider text-[var(--steel)]">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Stats - Shown below on smaller screens */}
        <div className="lg:hidden flex justify-center gap-8 mt-16 animate-fadeIn opacity-0 delay-400">
          {[
            { value: "3x", label: "Faster" },
            { value: "60%", label: "Cost Cut" },
            { value: "99.9%", label: "Quality" },
          ].map((stat, index) => (
            <div key={stat.value} className="text-center">
              <div className="headline text-2xl text-[var(--laser-red)] text-glow-red">{stat.value}</div>
              <div className="body-text text-xs uppercase tracking-wider text-[var(--steel)]">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Border */}
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[var(--iron)] to-transparent" />
    </section>
  );
};

// AI Advantage Section
const AIAdvantageSection = () => {
  const advantages = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: "3x Faster Delivery",
      description: "AI-powered discovery, design, and development compress timelines from months to weeks. We automate repetitive tasks, generate intelligent code scaffolds, and accelerate testing—without sacrificing quality.",
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Precision Engineering",
      description: "AI analyzes requirements, identifies edge cases, and suggests optimal architectures. Our systems catch potential issues before they become problems, ensuring robust, production-ready products.",
    },
    {
      icon: <RefreshCw className="w-8 h-8" />,
      title: "Continuous Innovation",
      description: "From intelligent code reviews to predictive analytics, AI empowers us to iterate rapidly, optimize continuously, and deliver solutions that evolve with your business needs.",
    },
  ];

  return (
    <section
      id="why-us"
      data-testid="ai-advantage-section"
      className="relative py-32 bg-[var(--bg-darker)] noise-overlay"
    >
      {/* Pulsing accent line */}
      <div className="pulse-line-horizontal absolute top-0 left-0 w-full h-px" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-20">
          <div
            data-testid="advantage-tag"
            className="inline-flex items-center gap-3 mb-6"
          >
            <span className="w-12 h-px bg-[var(--laser-red)]" />
            <span className="body-text text-xs uppercase tracking-[0.3em] text-[var(--laser-red)]">The AI Advantage</span>
          </div>
          <h2
            data-testid="advantage-title"
            className="headline text-4xl md:text-6xl text-[var(--white)] mb-6"
          >
            Speed Meets<br />
            <span className="text-outline-red">Excellence</span>
          </h2>
          <p
            data-testid="advantage-description"
            className="body-text text-lg text-[var(--steel-light)] max-w-2xl"
          >
            Our AI-driven approach doesn't just accelerate delivery—it elevates quality, reduces risk, and ensures your MVP reaches market with precision and polish.
          </p>
        </div>

        {/* Advantage Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {advantages.map((advantage, index) => (
            <div
              key={advantage.title}
              data-testid={`advantage-card-${index + 1}`}
              className="card-industrial group"
            >
              <div className="text-[var(--laser-red)] mb-6 group-hover:animate-pulse-slow">
                {advantage.icon}
              </div>
              <h3 className="headline text-xl text-[var(--white)] mb-4">
                {advantage.title}
              </h3>
              <p className="body-text text-[var(--steel-light)] leading-relaxed">
                {advantage.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      number: "01",
      title: "Product Discovery & Strategy",
      description: "We partner with you to define vision, validate market fit, and chart the optimal path to launch.",
      features: ["AI-powered market analysis", "User research & persona development", "Competitive intelligence", "Product roadmap planning"],
    },
    {
      number: "02",
      title: "Product Design & UX",
      description: "Beautiful, intuitive experiences that users love—designed with precision and validated through AI-enhanced insights.",
      features: ["Experience design & prototyping", "UI/UX optimization", "Design systems creation", "Accessibility & inclusivity"],
    },
    {
      number: "03",
      title: "Custom Software Development",
      description: "Scalable, secure, and performant solutions built with modern technologies and AI-accelerated development.",
      features: ["Full-stack development", "Cloud-native architecture", "API & integration services", "Legacy system modernization"],
    },
    {
      number: "04",
      title: "Quality Assurance & Testing",
      description: "Comprehensive testing strategies powered by AI to ensure flawless performance and exceptional reliability.",
      features: ["Automated testing frameworks", "Performance optimization", "Security audits", "Continuous monitoring"],
    },
  ];

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-32 bg-[var(--bg-dark)] grid-pattern"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-20">
          <div
            data-testid="services-tag"
            className="inline-flex items-center gap-3 mb-6"
          >
            <span className="w-12 h-px bg-[var(--laser-red)]" />
            <span className="body-text text-xs uppercase tracking-[0.3em] text-[var(--laser-red)]">What We Do</span>
          </div>
          <h2
            data-testid="services-title"
            className="headline text-4xl md:text-6xl text-[var(--white)] mb-6"
          >
            End-to-End<br />
            <span className="text-[var(--steel)]">Product Engineering</span>
          </h2>
          <p
            data-testid="services-description"
            className="body-text text-lg text-[var(--steel-light)] max-w-2xl"
          >
            From initial concept to market launch, we guide your product through every phase with expertise, innovation, and AI-driven efficiency.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {services.map((service, index) => (
            <div
              key={service.number}
              data-testid={`service-card-${service.number}`}
              className="card-industrial group relative"
            >
              {/* Service Number */}
              <div className="absolute -top-4 -right-4 headline text-8xl text-[var(--iron-dark)] opacity-50 group-hover:text-[var(--iron)] group-hover:opacity-30 transition-all duration-500 select-none">
                {service.number}
              </div>
              
              <div className="relative z-10">
                <h3 className="headline text-2xl text-[var(--white)] mb-4 group-hover:text-[var(--laser-red)] transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="body-text text-[var(--steel-light)] mb-6">
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-3">
                      <ChevronRight className="w-4 h-4 text-[var(--laser-red)]" />
                      <span className="body-text text-sm text-[var(--steel)]">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = () => {
  const steps = [
    {
      number: "1",
      title: "Discover",
      description: "AI-powered research and analysis uncover market opportunities, user needs, and technical requirements. We validate assumptions and define success metrics before writing a single line of code.",
      outcome: "Product strategy, technical roadmap, resource planning, and validated business case ready for execution.",
    },
    {
      number: "2",
      title: "Design",
      description: "Intelligent design tools accelerate prototyping and user testing. We create intuitive experiences backed by data-driven insights and user validation.",
      outcome: "Interactive prototypes, design systems, user flows, and validated designs ready for development.",
    },
    {
      number: "3",
      title: "Develop",
      description: "AI assists with code generation, reviews, and optimization while our expert engineers build scalable, maintainable solutions using best practices and modern architectures.",
      outcome: "Production-ready code, comprehensive documentation, automated deployments, and continuous integration pipelines.",
    },
    {
      number: "4",
      title: "Deliver",
      description: "Intelligent testing and monitoring ensure quality at every level. We orchestrate seamless launches and provide ongoing optimization to maximize impact.",
      outcome: "Successful product launch, performance monitoring, user feedback integration, and continuous improvement strategy.",
    },
  ];

  return (
    <section
      id="process"
      data-testid="process-section"
      className="relative py-32 bg-[var(--bg-darker)]"
    >
      {/* Pulsing accent line */}
      <div className="pulse-line-vertical absolute top-0 left-1/2 w-px h-full opacity-30" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-20">
          <div
            data-testid="process-tag"
            className="inline-flex items-center gap-3 mb-6"
          >
            <span className="w-12 h-px bg-[var(--laser-red)]" />
            <span className="body-text text-xs uppercase tracking-[0.3em] text-[var(--laser-red)]">How We Work</span>
          </div>
          <h2
            data-testid="process-title"
            className="headline text-4xl md:text-6xl text-[var(--white)] mb-6"
          >
            AI-Enhanced<br />
            <span className="text-outline-red">SDLC</span>
          </h2>
          <p
            data-testid="process-description"
            className="body-text text-lg text-[var(--steel-light)] max-w-2xl"
          >
            Our proven process combines agile methodology with AI capabilities to deliver exceptional results, faster.
          </p>
        </div>

        {/* Process Timeline */}
        <div className="relative">
          {/* Vertical Line */}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[var(--laser-red)] via-[var(--iron)] to-transparent" />

          <div className="space-y-16">
            {steps.map((step, index) => (
              <div
                key={step.number}
                data-testid={`process-step-${step.number}`}
                className={`relative flex flex-col md:flex-row gap-8 md:gap-16 ${
                  index % 2 === 1 ? "md:flex-row-reverse" : ""
                }`}
              >
                {/* Step Number */}
                <div className="absolute left-0 md:left-1/2 md:-translate-x-1/2 w-12 h-12 bg-[var(--bg-darker)] border-2 border-[var(--laser-red)] flex items-center justify-center z-10">
                  <span className="headline text-lg text-[var(--laser-red)]">{step.number}</span>
                </div>

                {/* Content */}
                <div className={`flex-1 pl-20 md:pl-0 ${
                  index % 2 === 1 ? "md:text-right md:pr-24" : "md:pl-24"
                }`}>
                  <h3 className="headline text-2xl text-[var(--white)] mb-4">{step.title}</h3>
                  <p className="body-text text-[var(--steel-light)] mb-6">{step.description}</p>
                  <div className="inline-flex items-start gap-3 p-4 bg-[var(--iron-darker)] border border-[var(--iron)]">
                    <span className="headline text-xs uppercase tracking-wider text-[var(--laser-red)] whitespace-nowrap">Outcomes:</span>
                    <span className="body-text text-sm text-[var(--steel)]">{step.outcome}</span>
                  </div>
                </div>

                {/* Empty space for alternating layout */}
                <div className="hidden md:block flex-1" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const stats = [
    { value: "3x", label: "Faster Time to Market" },
    { value: "60%", label: "Cost Reduction" },
    { value: "99.9%", label: "Quality Assurance" },
    { value: "100%", label: "Client Satisfaction" },
  ];

  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      data-testid="stats-section"
      className="relative py-24 bg-[var(--laser-red)] overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 grid-pattern" style={{ backgroundSize: '30px 30px' }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4">
          {stats.map((stat, index) => (
            <div
              key={stat.label}
              data-testid={`stat-item-${index + 1}`}
              className={`text-center transition-all duration-700 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="headline text-5xl md:text-7xl text-[var(--white)] mb-2">
                {stat.value}
              </div>
              <div className="body-text text-xs md:text-sm uppercase tracking-wider text-[var(--white)]/80">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = ({ onOpenContact }) => {
  return (
    <section
      id="contact"
      data-testid="cta-section"
      className="relative py-32 bg-[var(--bg-dark)] grid-pattern noise-overlay"
    >
      {/* Pulsing accent lines */}
      <div className="pulse-line-horizontal absolute top-1/4 left-0 w-full h-px opacity-30" />
      <div className="pulse-line-horizontal absolute bottom-1/4 left-0 w-full h-px opacity-30" style={{ animationDelay: "2s" }} />
      
      {/* Decorative glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[var(--laser-red)]/10 blur-[150px] pointer-events-none" />

      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <div
          data-testid="cta-box"
          className="relative p-12 md:p-20 border border-[var(--iron)] bg-gradient-to-b from-[var(--iron-darker)] to-[var(--bg-darker)]"
        >
          {/* Corner Accents */}
          <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[var(--laser-red)]" />
          <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-[var(--laser-red)]" />
          <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-[var(--laser-red)]" />
          <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[var(--laser-red)]" />

          <h2
            data-testid="cta-title"
            className="headline text-3xl md:text-5xl text-[var(--white)] mb-6"
          >
            Ready to Transform<br />
            <span className="text-outline">Your Vision?</span>
          </h2>
          <p
            data-testid="cta-description"
            className="body-text text-lg text-[var(--steel-light)] mb-10 max-w-xl mx-auto"
          >
            Let's discuss how Stragmato can accelerate your product journey with AI-powered engineering excellence.
          </p>
          <button
            onClick={onOpenContact}
            data-testid="cta-email-button"
            className="btn-industrial btn-primary-ind inline-flex items-center group"
          >
            <span>Get in Touch</span>
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = ({ onOpenContact }) => {
  return (
    <footer
      data-testid="footer"
      className="relative py-12 bg-[var(--bg-darker)] border-t border-[var(--iron)]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <a
            href="#"
            data-testid="footer-logo"
            className="logo-prominent text-2xl tracking-tight text-[var(--white)] hover:text-[var(--laser-red)] transition-colors duration-300"
          >
            <span className="font-black italic">STRAG</span><span className="font-light">MATO</span>
          </a>
          
          <div className="flex items-center gap-8">
            <button
              onClick={onOpenContact}
              data-testid="footer-contact-btn"
              className="body-text text-sm text-[var(--steel-light)] hover:text-[var(--laser-red)] transition-colors duration-300"
            >
              hello@stragmato.com
            </button>
          </div>

          <p
            data-testid="footer-copyright"
            className="body-text text-sm text-[var(--steel)]"
          >
            © 2026 Stragmato. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  const [isContactOpen, setIsContactOpen] = useState(false);

  const openContact = () => setIsContactOpen(true);
  const closeContact = () => setIsContactOpen(false);

  return (
    <div className="App" data-testid="app-container">
      <Navigation onOpenContact={openContact} />
      <HeroSection onOpenContact={openContact} />
      <AIAdvantageSection />
      <ServicesSection />
      <ProcessSection />
      <StatsSection />
      <CTASection onOpenContact={openContact} />
      <Footer onOpenContact={openContact} />
      <ContactModal isOpen={isContactOpen} onClose={closeContact} />
    </div>
  );
}

export default App;
